import { Button } from '@/components/Button/Button';
import { LoadingSpinner } from '@/components/Loading';
import { Input } from '@/components/ui/Input';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { cn } from '@/utils/utils';
import { X } from 'lucide-react';
import { type KeyboardEvent, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const emailSchema = z.string().email('Invalid email format');

interface EmailTag {
  id: string;
  value: string;
  isValid: boolean;
}

export function OnboardingReferral({
  affiliateUrl,
  isUpsertingAffiliate,
  handleNextStep,
}: {
  affiliateUrl: string | null;
  isUpsertingAffiliate: boolean;
  handleNextStep: () => void;
}) {
  const [isTagMode, setIsTagMode] = useState(false);
  const [emails, setEmails] = useState<string[]>(['', '', '']);
  const [tags, setTags] = useState<EmailTag[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [errors, setErrors] = useState<string[]>(['', '', '']);
  const { track } = useAnalytics();
  const tagInputRef = useRef<HTMLInputElement>(null);
  const { mutate: sendInvite, isPending } =
    api.referral.sendReferralEmails.useMutation();

  const validateEmail = (
    email: string,
  ): { isValid: boolean; error?: string } => {
    if (!email) return { isValid: true };
    const result = emailSchema.safeParse(email);
    return {
      isValid: result.success,
      error: result.success ? undefined : result.error.errors[0].message,
    };
  };

  const getValidEmails = () => {
    return isTagMode
      ? tags?.filter((tag) => tag.isValid).map((tag) => tag.value)
      : emails?.filter((email) => email && validateEmail(email).isValid);
  };

  const emailsToSend = getValidEmails();

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    const newErrors = [...errors];
    const validation = validateEmail(value);
    newErrors[index] = validation.error || '';
    setErrors(newErrors);
  };

  const switchToTagMode = () => {
    const validEmails = emails.filter(
      (email) => email && validateEmail(email).isValid,
    );
    const newTags = validEmails.map((email) => ({
      id: Math.random().toString(36).substring(2, 9),
      value: email,
      isValid: true,
    }));

    setTags(newTags);
    setIsTagMode(true);
    setInputValue('');

    setTimeout(() => {
      if (tagInputRef.current) {
        tagInputRef.current.focus();
      }
    }, 0);
    track('onboarding_referral_toggle_invites');
  };

  const addTag = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    const validation = validateEmail(trimmedValue);
    const newTag = {
      id: Math.random().toString(36).substring(2, 9),
      value: trimmedValue,
      isValid: validation.isValid,
    };

    setTags([...tags, newTag]);
    setInputValue('');
  };

  const removeTag = (id: string) => {
    setTags(tags.filter((tag) => tag.id !== id));
  };

  const handleTagInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove the last tag when backspace is pressed and input is empty
      const newTags = [...tags];
      newTags.pop();
      setTags(newTags);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText.split(/[,;\s]+/);

    for (const email of emails) {
      if (email.trim()) {
        addTag(email.trim());
      }
    }
  };

  const handleCopyAffiliateUrl = () => {
    if (!affiliateUrl) return;
    track('onboarding_referral_copy_link');

    navigator.clipboard.writeText(affiliateUrl);
    toast('Invite link copied to clipboard');
  };

  const handleSubmit = () => {
    const emailsToSend = getValidEmails();

    if (emailsToSend.length === 0) {
      handleNextStep();
      return;
    }
    if (!affiliateUrl) return;

    track('onboarding_referral_submit', {
      emails_total_referred: emailsToSend.length,
    });

    sendInvite(
      { emails: emailsToSend, affiliateUrl },
      {
        onSuccess: () => {
          handleNextStep();
        },
      },
    );
  };
  return (
    <div className="bg-background p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl leading-[120%] font-semibold mb-2">
          Invite and earn $10 cash for every friend that joins
        </h2>
        <p className="text-muted-foreground font-medium text-sm leading-[130%]">
          You get $10 cash, they get $10 off
        </p>
      </div>

      <div className="space-y-2">
        {!isTagMode ? (
          <>
            {emails.map((email, index) => (
              <div key={`${index + 1}sdaa`} className="relative">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  className={cn(
                    'w-full h-11  rounded-xl border-2',
                    errors[index] && 'border-destructive-solid',
                  )}
                />
                {errors[index] && (
                  <p className="text-red-500 text-xs mt-1">{errors[index]}</p>
                )}
              </div>
            ))}
            <div className="flex items-center justify-center w-full pb-3">
              <button
                onClick={switchToTagMode}
                className="hover:bg-muted px-2 py-1 rounded-md text-muted-foreground font-medium text-[13px] leading-[20px] tracking-[-0.32px] flex items-center justify-center"
              >
                + Add more invites
              </button>
            </div>
          </>
        ) : (
          <div
            className="border-2 bg-background rounded-md p-2 min-h-32 max-h-44 mb-4 cursor-text overflow-y-auto"
            onClick={() => {
              if (tagInputRef.current) {
                tagInputRef.current.focus();
              }
            }}
          >
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className={`flex items-center gap-1.5 px-1.5 py-1 rounded-md text-sm ${
                    tag.isValid
                      ? 'bg-popover-hover-solid'
                      : 'bg-destructive border-destructive-solid'
                  }`}
                >
                  <span>{tag.value}</span>
                  <button
                    type="button"
                    onClick={() => removeTag(tag.id)}
                    className="text-muted-foreground2/50 hover:text-muted-foreground2"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <input
                ref={tagInputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                onPaste={handlePaste}
                onBlur={() => {
                  if (inputValue) {
                    addTag(inputValue);
                  }
                }}
                className="flex-grow bg-transparent min-w-[120px] outline-none border-none text-sm tracking-[-0.24px]"
                placeholder={tags.length === 0 ? 'Enter emails' : ''}
              />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Button
          className="w-full  rounded-xl h-11"
          onClick={handleSubmit}
          disabled={isPending || (emailsToSend.length > 0 && !affiliateUrl)}
        >
          {isPending ? (
            <LoadingSpinner variant="small" color="gray" />
          ) : emailsToSend.length > 0 ? (
            'Invite and take me to Anara'
          ) : (
            'Take me to Anara'
          )}
        </Button>
        <Button
          variant="secondary"
          className="w-full rounded-xl h-11"
          onClick={handleCopyAffiliateUrl}
          disabled={!affiliateUrl || isUpsertingAffiliate}
        >
          {isUpsertingAffiliate ? (
            <LoadingSpinner variant="small" color="gray" />
          ) : (
            'Copy invite link'
          )}
        </Button>
      </div>

      <div className="text-center text-sm text-textGray4 mt-4">
        Join 2M+ researchers
      </div>
    </div>
  );
}

export default OnboardingReferral;
